import { useState, useEffect, useRef } from "react";
import type { User } from "@supabase/supabase-js";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { TERMIN_SPALTEN_OHNE_LINKS } from "@/lib/termine/zugangslinks";
import { getTerminPrimaryUrl, hasCustomLandingPage } from "@/lib/termine/getTerminPrimaryUrl";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Clock, Users, ExternalLink, Info, Sparkles, Grid3x3, List, Share2, Download, ArrowUp, Search, CalendarDays, ChevronLeft, ChevronRight, Loader2, CalendarPlus, Check, GraduationCap, Guitar, Music, BookOpen, Video } from "lucide-react";
import { format, differenceInDays, differenceInHours, addDays, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay } from "date-fns";
import { de } from "date-fns/locale";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { ALL_TRAINING_BUNDLE_IDS } from "@/config/trainingBundles";
import SEO from "@/components/SEO";
import { isMemberFreeTerminTyp } from "@/lib/termine/memberFreeTermin";
import { useKommendeInfoveranstaltungen } from "@/hooks/useKommendeInfoveranstaltungen";
import { INFO_TYP, infoPasst, mischen, nachMonatGemischt, type KommendeInfo } from "@/lib/termine/infoveranstaltungen";
import { InfoKalenderEintrag, InfoListenKarte, InfoRasterKarte } from "@/components/termine/InfoveranstaltungAlt";


// Category-specific hero configurations for Termine
const categoryHeroConfig: Record<string, {
  badge: string;
  badgeIcon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  blob1: string;
  blob2: string;
  accentColor: string;
}> = {
  alle: {
    badge: "Live-Webinare",
    badgeIcon: <Sparkles className="w-4 h-4" />,
    title: "Aktuelle Termine",
    description: "Entdecke unsere Live-Webinare und erweitere deine musikalischen Fähigkeiten",
    gradient: "from-primary/10 via-background to-background",
    blob1: "bg-primary/15",
    blob2: "bg-accent/10",
    accentColor: "text-primary",
  },
  "Ausbildungs-Webinare (Fachkraft für Musik)": {
    badge: "Fachkraft für Musik",
    badgeIcon: <GraduationCap className="w-4 h-4" />,
    title: "Ausbildungs-Webinare",
    description: "Exklusive Live-Webinare für Teilnehmer:innen der Fachkraft-Ausbildung für Musik",
    gradient: "from-primary/10 via-primary/5 to-background",
    blob1: "bg-primary/15",
    blob2: "bg-primary/10",
    accentColor: "text-primary",
  },
  "Ausbildungs-Webinare (Austauschgruppe)": {
    badge: "Austauschgruppe",
    badgeIcon: <GraduationCap className="w-4 h-4" />,
    title: "Offene Austauschgruppe",
    description: "Monatlicher Austausch für Teilnehmer:innen der Ausbildungen Fachkraft für Musik und Fachkraft für Musik und Sprache",
    gradient: "from-primary/10 via-primary/5 to-background",
    blob1: "bg-primary/15",
    blob2: "bg-primary/10",
    accentColor: "text-primary",
  },
  "Ausbildungs-Webinare (Päd. Fachkraft Gitarre)": {
    badge: "Fachkraft Gitarre",
    badgeIcon: <Guitar className="w-4 h-4" />,
    title: "Gitarren-Webinare",
    description: "Live-Webinare speziell für angehende pädagogische Fachkräfte für Gitarre",
    gradient: "from-accent/10 via-accent/5 to-background",
    blob1: "bg-accent/15",
    blob2: "bg-accent/10",
    accentColor: "text-accent",
  },
  "Offene Webinare": {
    badge: "Offene Webinare",
    badgeIcon: <Video className="w-4 h-4" />,
    title: "Offene Webinare für alle",
    description: "Kostenlose und kostenpflichtige Live-Webinare – offen für alle Interessierten",
    gradient: "from-green-500/10 via-green-500/5 to-background",
    blob1: "bg-green-500/15",
    blob2: "bg-emerald-500/10",
    accentColor: "text-green-600",
  },
  "Kostenlose Webinare": {
    badge: "Kostenlos",
    badgeIcon: <Sparkles className="w-4 h-4" />,
    title: "Kostenlose Webinare",
    description: "Gratis Live-Webinare für alle Interessierten",
    gradient: "from-green-500/10 via-green-500/5 to-background",
    blob1: "bg-green-500/15",
    blob2: "bg-emerald-500/10",
    accentColor: "text-green-600",
  },
  "Musik-Kita Webinare": {
    badge: "Musik-Kita",
    badgeIcon: <Music className="w-4 h-4" />,
    title: "Webinare für Musik-Kitas",
    description: "Spezielle Fortbildungen und Live-Sessions für zertifizierte Musik-Kitas",
    gradient: "from-blue-500/10 via-blue-500/5 to-background",
    blob1: "bg-blue-500/15",
    blob2: "bg-cyan-500/10",
    accentColor: "text-blue-600",
  },
  "Themenwebinare": {
    badge: "Themenwebinare",
    badgeIcon: <BookOpen className="w-4 h-4" />,
    title: "Themenwebinare",
    description: "Vertiefe dein Wissen in speziellen Themenbereichen der musikalischen Früherziehung",
    gradient: "from-purple-500/10 via-purple-500/5 to-background",
    blob1: "bg-purple-500/15",
    blob2: "bg-violet-500/10",
    accentColor: "text-purple-600",
  },
  "Liederwerkstatt Gitarrissimo": {
    badge: "Gitarrissimo",
    badgeIcon: <Guitar className="w-4 h-4" />,
    title: "Liederwerkstatt Gitarrissimo",
    description: "Kreative Liedbegleitung mit der Gitarre – für Erzieher:innen und Pädagog:innen",
    gradient: "from-orange-500/10 via-orange-400/5 to-background",
    blob1: "bg-orange-500/15",
    blob2: "bg-amber-500/10",
    accentColor: "text-orange-600",
  },
  "Liederwerkstatt Vokalissimo": {
    badge: "Vokalissimo",
    badgeIcon: <Music className="w-4 h-4" />,
    title: "Liederwerkstatt Vokalissimo",
    description: "Stimmbildung und Gesang in der musikalischen Früherziehung",
    gradient: "from-rose-500/10 via-rose-400/5 to-background",
    blob1: "bg-rose-500/15",
    blob2: "bg-pink-500/10",
    accentColor: "text-rose-600",
  },
  "Liederwerkstatt Ukulissimo": {
    badge: "Ukulissimo",
    badgeIcon: <Music className="w-4 h-4" />,
    title: "Liederwerkstatt Ukulissimo",
    description: "Liedbegleitung mit der Ukulele – leicht erlernbar und vielseitig einsetzbar",
    gradient: "from-teal-500/10 via-teal-400/5 to-background",
    blob1: "bg-teal-500/15",
    blob2: "bg-cyan-500/10",
    accentColor: "text-teal-600",
  },
};

type Termin = {
  id: string;
  titel: string;
  slug?: string;
  dozent?: string;
  zielgruppe?: string;
  beschreibung?: string;
  inhalte?: string;
  sonstiges_ueberschrift?: string;
  sonstiges_inhalt?: string;
  datum: string;
  dauer_minuten: number;
  typ: string;
  max_teilnehmer?: number;
  status: string;
  bild_url?: string;
  video_url?: string;
  zugangstyp: 'öffentlich_kostenpflichtig' | 'öffentlich_kostenlos' | 'nur_ausbildung' | 'nur_kursteilnehmer';
  preis_normal?: number;
  ausbildungs_info_text?: string;
  ausbildungs_link?: string;
  info_link_text?: string;
  info_link_url?: string;
  stripe_price_id?: string;
  stripe_product_id?: string;
  landing_page_url?: string | null;
};

// Sitzung 14b: Infoveranstaltungen mit denselben Filtern wie die Termine (eigene Tabellen, gemischt erst beim Zeichnen).
const infosGefiltert = (infos: KommendeInfo[], kategorie: string, schnell: string, suche: string) =>
  infos.filter((i) => infoPasst(i, { kategorie, schnell, suche }));

const Termine = () => {
  const navigate = useNavigate();
  const { tier } = useSubscription();
  const isMember = tier === "pro";

  // Check if a termin is free for the current logged-in member (zentrale Regel, memberFreeTermin.ts)
  const isTerminFreeForUser = (termin: Termin): boolean => {
    if (termin.zugangstyp === "öffentlich_kostenlos" || !termin.preis_normal) return true;
    if (!isMember) return false;
    return isMemberFreeTerminTyp(termin.typ);
  };
  const [termine, setTermine] = useState<Termin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTyp, setSelectedTyp] = useState<string>(searchParams.get('kategorie') || "alle");
  const [detailTermin, setDetailTermin] = useState<Termin | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'calendar'>('list');
  const [quickFilter, setQuickFilter] = useState<'alle' | 'kostenlos' | 'diese-woche' | 'themenwebinare'>('alle');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [bookingInProgress, setBookingInProgress] = useState<string | null>(null);
  const [reminderInProgress, setReminderInProgress] = useState<string | null>(null);
  const [guestBookingTermin, setGuestBookingTermin] = useState<Termin | null>(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestStreet, setGuestStreet] = useState("");
  const [guestCity, setGuestCity] = useState("");
  const [guestZip, setGuestZip] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successTerminTitle, setSuccessTerminTitle] = useState("");
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAusbildungsteilnehmer, setIsAusbildungsteilnehmer] = useState(false);
  const [addedToCalendar, setAddedToCalendar] = useState<Set<string>>(new Set());
  const termineListRef = useRef<HTMLDivElement>(null);
  // Infoveranstaltungen zwischen den Terminen (Sitzung 14b): eigene Tabellen, gemischt erst beim Zeichnen
  const infoveranstaltungen = useKommendeInfoveranstaltungen();

  // Get hero config for current category
  const heroConfig = selectedTyp && categoryHeroConfig[selectedTyp] 
    ? categoryHeroConfig[selectedTyp] 
    : categoryHeroConfig.alle;

  // Scroll listener for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper: Get countdown text
  const getCountdown = (datum: string) => {
    const now = new Date();
    const eventDate = new Date(datum);
    const days = differenceInDays(eventDate, now);
    const hours = differenceInHours(eventDate, now);

    if (days > 7) return null;
    if (days > 0) return `In ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
    if (hours > 0) return `In ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
    return 'Heute';
  };

  const formatEurFromCents = (cents?: number | null) => {
    if (cents === null || cents === undefined) return "—";
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
  };

  // Helper: Get category color
  const getCategoryColor = (typ: string) => {
    const colors: Record<string, string> = {
      'Ausbildungs-Webinare (Fachkraft für Musik)': 'border-l-primary',
      'Ausbildungs-Webinare (Austauschgruppe)': 'border-l-primary',
      'Ausbildungs-Webinare (Päd. Fachkraft Gitarre)': 'border-l-accent',
      'Offene Webinare': 'border-l-green-500',
      'Musik-Kita Webinare': 'border-l-blue-500',
    };
    return colors[typ] || 'border-l-primary/50';
  };

  // Helper: Generate .ics file
  const downloadICS = (termin: Termin) => {
    const start = new Date(termin.datum);
    const end = new Date(start.getTime() + termin.dauer_minuten * 60000);
    
    const formatDate = (date: Date) => {
      // Regex mit String.fromCharCode um Tailwind CSS Scanner zu umgehen
      const dashColonRegex = new RegExp('[' + String.fromCharCode(45, 58) + ']', 'g');
      return date.toISOString().replace(dashColonRegex, '').split('.')[0] + 'Z';
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//MusiKonzept//Termine//DE
BEGIN:VEVENT
UID:${termin.id}@musikonzept.com
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(start)}
DTEND:${formatDate(end)}
SUMMARY:${termin.titel}
DESCRIPTION:${termin.beschreibung?.replace(/<[^>]*>/g, '').substring(0, 200) || ''}
LOCATION:Online
URL:${window.location.origin}/termine?termin=${termin.id}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${termin.titel.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`;
    link.click();
    toast.success('Termin zum Kalender hinzugefügt');
  };

  // Helper: Share functionality
  const shareTermin = (termin: Termin) => {
    const url = `${window.location.origin}/termine?termin=${termin.id}`;
    
    if (navigator.share) {
      navigator.share({
        title: termin.titel,
        text: `${termin.titel} - ${format(new Date(termin.datum), "d. MMMM yyyy", { locale: de })}`,
        url: url,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link in Zwischenablage kopiert');
    }
  };

  // Check user and Ausbildungsteilnehmer status
  useEffect(() => {
    const checkUserStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      setCurrentUser(user);
      
      if (user) {
        // M-4 fix: Run independent queries in parallel
        const [enrollmentResult, bookingsResult] = await Promise.all([
          supabase
            .from("bundle_enrollments")
            .select("id")
            .eq("user_id", user.id)
            .in("bundle_id", ALL_TRAINING_BUNDLE_IDS)
            .limit(1)
            .maybeSingle(),
          supabase
            .from("termin_bookings")
            .select("termin_id")
            .eq("user_id", user.id)
            .neq("payment_status", "cancelled"),
        ]);
        
        setIsAusbildungsteilnehmer(!!enrollmentResult.data);
        
        if (bookingsResult.data) {
          setAddedToCalendar(new Set(bookingsResult.data.map(b => b.termin_id)));
        }
      }
    };
    
    checkUserStatus();
  }, []);

  useEffect(() => {
    loadTermine();
  }, []);

  useEffect(() => {
    const kategorie = searchParams.get('kategorie');
    if (kategorie) {
      setSelectedTyp(kategorie);
    }
  }, [searchParams]);

  useEffect(() => {
    // If URL has termin param, redirect to landing page
    const terminId = searchParams.get('termin');
    if (terminId && termine.length > 0) {
      const termin = termine.find(t => t.id === terminId);
      const url = termin ? getTerminPrimaryUrl(termin) : null;
      if (url) {
        navigate(url);
      } else if (termin) {
        // Fallback: open detail modal for termine without slug
        setDetailTermin(termin);
      }
    }
  }, [termine, searchParams, navigate]);

  const loadTermine = async () => {
    const { data, error } = await supabase
      .from('termine')
      .select(TERMIN_SPALTEN_OHNE_LINKS)
      .eq('status', 'live')
      .gte('datum', new Date().toISOString())
      .order('datum', { ascending: true });

    if (!error && data) {
      setTermine(data as Termin[]);
    }
    setIsLoading(false);
  };

  // Fixed order of categories as requested
  const sortedTypenOrder = [
    "alle",
    "Kostenlose Webinare",
    "Themenwebinare",
    "Gitarren-Kickstart",
    "Liederwerkstatt Gitarrissimo",
    "Liederwerkstatt Vokalissimo",
    "Liederwerkstatt Ukulissimo",
    "Ausbildungs-Webinare (Fachkraft für Musik)",
    "Ausbildungs-Webinare (Austauschgruppe)",
    "Ausbildungs-Webinare (Kita-Gitarrenkurs)",
  ];
  
  // Define groups for visual styling
  const liederwerkstattTypes = ["Liederwerkstatt Gitarrissimo", "Liederwerkstatt Vokalissimo", "Liederwerkstatt Ukulissimo"];
  const ausbildungTypes = ["Ausbildungs-Webinare (Fachkraft für Musik)", "Ausbildungs-Webinare (Austauschgruppe)", "Ausbildungs-Webinare (Kita-Gitarrenkurs)"];
  
  const existingTypen = new Set(termine.map(t => t.typ));
  const uniqueTypen = sortedTypenOrder.filter(typ => typ === "alle" || existingTypen.has(typ));
  
  const filteredTermine = termine.filter((termin) => {
    // Type filter
    const typeMatch = selectedTyp === "alle" || termin.typ === selectedTyp;
    
    // Quick filter
    let quickFilterMatch = true;
    if (quickFilter === 'kostenlos') {
      quickFilterMatch = termin.zugangstyp === 'öffentlich_kostenlos';
    } else if (quickFilter === 'diese-woche') {
      const weekFromNow = addDays(startOfDay(new Date()), 7);
      quickFilterMatch = new Date(termin.datum) <= weekFromNow;
    } else if (quickFilter === 'themenwebinare') {
      quickFilterMatch = termin.typ === 'Themenwebinare';
    }
    
    // Search filter
    if (!searchQuery) return typeMatch && quickFilterMatch;
    
    const query = searchQuery.toLowerCase();
    const beschreibungText = termin.beschreibung?.replace(/<[^>]*>/g, '').toLowerCase() || '';
    return typeMatch && quickFilterMatch && (
      termin.titel.toLowerCase().includes(query) ||
      termin.typ.toLowerCase().includes(query) ||
      beschreibungText.includes(query)
    );
  });

  // Get next upcoming event for hero
  const nextTermin = termine[0];

  // Group termine by month
  const termineByMonth = filteredTermine.reduce((acc, termin) => {
    const monthKey = format(new Date(termin.datum), "MMMM yyyy", { locale: de });
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(termin);
    return acc;
  }, {} as Record<string, Termin[]>);

  // Get related events (same category)
  const getRelatedTermine = (currentTermin: Termin) => {
    return termine
      .filter(t => t.id !== currentTermin.id && t.typ === currentTermin.typ)
      .slice(0, 3);
  };

  // Get events for a specific day
  const getTermineForDay = (day: Date) => {
    return filteredTermine.filter(termin => 
      isSameDay(new Date(termin.datum), day)
    );
  };

  // Generate calendar days
  const getCalendarDays = () => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days at start to align with weekday
    const startDay = getDay(start);
    const paddingStart = startDay === 0 ? 6 : startDay - 1; // Monday = 0
    
    const paddedDays = [
      ...Array(paddingStart).fill(null),
      ...days
    ];
    
    return paddedDays;
  };

  const getVideoEmbedUrl = (url: string) => {
    if (!url) return null;
    
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    
    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    
    // Loom
    if (url.includes('loom.com')) {
      const videoId = url.split('share/')[1]?.split('?')[0];
      return videoId ? `https://www.loom.com/embed/${videoId}` : null;
    }
    
    return url;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToTermineList = () => {
    termineListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleStatClick = (filter: 'alle' | 'kostenlos' | 'diese-woche' | 'themenwebinare') => {
    setQuickFilter(filter);
    setTimeout(scrollToTermineList, 100);
  };

  // Handle termin booking
  const handleBookTermin = async (
    termin: Termin, 
    guestData?: {
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
      street: string;
      city: string;
      zip: string;
    }
  ) => {
    // Termine mit eigener Landingpage: immer in neuem Tab öffnen (kein Buchungsflow)
    if (hasCustomLandingPage(termin)) {
      const url = getTerminPrimaryUrl(termin);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }


    // Skip checkout for "nur_ausbildung" - just link to info page
    if (termin.zugangstyp === 'nur_ausbildung') {
      window.open(termin.ausbildungs_link || termin.info_link_url || 'https://www.musikonzept.de/mein-bereich/bereich/fachkraft-fuer-musik', '_blank');
      return;
    }

    // For "nur_kursteilnehmer" - use register-termin (free, requires login + course enrollment)
    if (termin.zugangstyp === 'nur_kursteilnehmer') {
      setBookingInProgress(termin.id);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          toast.error('Bitte melde dich an, um dich für diesen Termin zu registrieren.');
          setBookingInProgress(null);
          return;
        }
        const { data, error } = await supabase.functions.invoke('register-termin', {
          body: { termin_id: termin.id }
        });
        if (error) {
          console.error('Booking error:', error);
          toast.error('Fehler bei der Buchung. Bitte versuche es erneut.');
        } else if (data?.already_registered) {
          toast.info('Du bist bereits für diesen Termin angemeldet.');
        } else if (data?.success) {
          setSuccessTerminTitle(termin.titel);
          setSuccessDialogOpen(true);
          setAddedToCalendar(prev => new Set([...prev, termin.id]));
        } else if (data?.error) {
          toast.error(data.error);
        }
        setDetailTermin(null);
      } catch (err) {
        console.error('Booking error:', err);
        toast.error('Fehler bei der Buchung.');
      } finally {
        setBookingInProgress(null);
      }
      return;
    }
    
    // Termine mit eigener Landingpage (z. B. kostenlose Infowebinare) immer im neuen Tab öffnen
    const primaryUrl = getTerminPrimaryUrl(termin);
    if (hasCustomLandingPage(termin) && primaryUrl) {
      window.open(primaryUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    setBookingInProgress(termin.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      
      // For FREE termin without user and no guest data provided, show guest booking dialog
      if (!user && termin.zugangstyp === 'öffentlich_kostenlos' && !guestData) {
        setGuestBookingTermin(termin);
        setBookingInProgress(null);
        return;
      }
      
      // If user is not logged in and it's a paid termin, proceed directly to Stripe checkout
      // Stripe Checkout will collect email and customer details
      
      // For FREE termin, use register-termin function directly
      if (termin.zugangstyp === 'öffentlich_kostenlos') {
        const body: any = { termin_id: termin.id };
        if (guestData) {
          body.guest_email = guestData.email;
          body.guest_name = `${guestData.firstName} ${guestData.lastName}`.trim();
          body.guest_first_name = guestData.firstName;
          body.guest_last_name = guestData.lastName;
          body.guest_phone = guestData.phone;
          body.guest_street = guestData.street;
          body.guest_city = guestData.city;
          body.guest_zip = guestData.zip;
        }
        
        const { data, error } = await supabase.functions.invoke('register-termin', { body });

        if (error) {
          console.error('Booking error:', error);
          toast.error('Fehler bei der Buchung. Bitte versuche es erneut.');
          return;
        }

        if (data?.already_registered) {
          toast.info('Du bist bereits für diesen Termin angemeldet.');
        } else if (data?.success) {
          // Show success dialog instead of toast
          setSuccessTerminTitle(termin.titel);
          setSuccessDialogOpen(true);
          setAddedToCalendar(prev => new Set([...prev, termin.id]));
        } else if (data?.error) {
          toast.error(data.error);
        }
        
        setDetailTermin(null);
        setGuestBookingTermin(null);
        // Reset guest form fields
        setGuestFirstName("");
        setGuestLastName("");
        setGuestEmail("");
        setGuestPhone("");
        setGuestStreet("");
        setGuestCity("");
        setGuestZip("");
        return;
      }
      
      // For PAID termin: IMMER unseren eigenen Checkout öffnen, niemals Stripe-Hosted
      if (termin.slug) {
        window.open(`/termin-checkout/${termin.slug}`, '_blank');
      } else {
        toast.error('Diesem Termin fehlt ein Slug für den Checkout.');
      }
      setBookingInProgress(null);
      setDetailTermin(null);
      return;
    } catch (err) {
      console.error('Booking error:', err);
      toast.error('Fehler bei der Buchung. Bitte versuche es erneut.');
    } finally {
      setBookingInProgress(null);
    }
  };


  // Handle adding Ausbildungstermin to personal calendar
  const handleAddToCalendar = async (termin: Termin) => {
    if (!currentUser || !isAusbildungsteilnehmer) {
      toast.error('Bitte melde dich an, um diesen Termin zu deiner Übersicht hinzuzufügen.');
      return;
    }
    
    setReminderInProgress(termin.id);
    try {
      const { data, error } = await supabase.functions.invoke('register-termin', {
        body: {
          termin_id: termin.id,
          is_reminder: true,
        },
      });

      if (error) {
        console.error('Reminder error:', error);
        toast.error('Fehler beim Hinzufügen. Bitte versuche es erneut.');
        return;
      }

      if (data?.already_registered) {
        toast.info('Dieser Termin ist bereits in deiner Übersicht.');
      } else if (data?.success) {
        setAddedToCalendar(prev => new Set([...prev, termin.id]));
        toast.success('Termin zu deiner Übersicht hinzugefügt!', {
          description: 'Du findest ihn unter "Meine Termine".'
        });
      }
    } catch (err) {
      console.error('Reminder error:', err);
      toast.error('Fehler beim Hinzufügen. Bitte versuche es erneut.');
    } finally {
      setReminderInProgress(null);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO title={`Termine – Live-Webinare & Veranstaltungen | MusiKonzept`} description={`Aktuelle Live-Webinare, Workshops und Veranstaltungen rund um Musikpädagogik und Kita-Praxis.`} path="/termine" />
      <Header />
      <main className="pt-20">
        {/* Hero Section - Dynamic based on category (like Kurse page) */}
        <section className="relative pt-16 pb-10 overflow-hidden transition-all duration-500">
          {/* Background - seamless gradient like Kurse */}
          <div className={`absolute inset-0 bg-gradient-to-b ${heroConfig.gradient} transition-all duration-500`} />
          <div className={`absolute top-0 left-1/4 w-96 h-96 ${heroConfig.blob1} rounded-full blur-3xl transition-all duration-500`} />
          <div className={`absolute bottom-0 right-1/4 w-96 h-96 ${heroConfig.blob2} rounded-full blur-3xl transition-all duration-500`} />

          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              {/* Dynamic Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm ${heroConfig.accentColor} mb-4 border border-current/20 transition-all duration-300`}>
                {heroConfig.badgeIcon}
                <span className="text-sm font-medium">{heroConfig.badge}</span>
              </div>
              
              {/* Title - BLACK text like Kurse */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 transition-all duration-300">
                {heroConfig.title}
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto transition-all duration-300">
                {heroConfig.description}
              </p>

              {/* Search Field in Hero - like Kurse */}
              <div className="max-w-md mx-auto mb-6">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Termine durchsuchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 h-12 text-base rounded-full bg-white/90 backdrop-blur-sm border-border shadow-sm focus:border-primary"
                  />
                </div>
              </div>

              {/* Category count */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/60 backdrop-blur-sm text-muted-foreground`}>
                <span className="text-sm">
                  {filteredTermine.length + infosGefiltert(infoveranstaltungen, selectedTyp, quickFilter, searchQuery).length}{" "}
                  {filteredTermine.length + infosGefiltert(infoveranstaltungen, selectedTyp, quickFilter, searchQuery).length === 1 ? "Termin" : "Termine"} verfügbar
                </span>
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 py-8" ref={termineListRef}>
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Termine werden geladen...</p>
            </div>
          ) : termine.length === 0 && infoveranstaltungen.length === 0 ? (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="text-center py-12">
                <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Aktuell keine Termine verfügbar</h3>
                <p className="text-muted-foreground">
                  Schauen Sie bald wieder vorbei oder abonnieren Sie unseren Newsletter.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Category Filter - Sticky like Kurse page */}
              <div className="md:sticky md:top-16 z-30 bg-background/95 backdrop-blur-sm border-b border-border/50 py-4 -mx-6 px-6 mb-6">
                {/* Category Buttons */}
                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  {(infoveranstaltungen.length > 0 ? [...uniqueTypen, INFO_TYP] : uniqueTypen).map((typ, index) => {
                    // Determine if this is first in a group (for left border)
                    const isLiederwerkstatt = liederwerkstattTypes.includes(typ);
                    const isAusbildung = ausbildungTypes.includes(typ);
                    const isFirstLiederwerkstatt = typ === "Liederwerkstatt Gitarrissimo" && existingTypen.has(typ);
                    const isFirstAusbildung = typ === "Ausbildungs-Webinare (Fachkraft für Musik)" && existingTypen.has(typ);
                    
                    // Group styling - subtle background tint
                    const groupStyles = isLiederwerkstatt 
                      ? "ring-1 ring-orange-300/50" 
                      : isAusbildung 
                        ? "ring-1 ring-primary/30" 
                        : "";
                    
                    return (
                      <div key={typ} className="flex items-center">
                        {/* Group divider before Liederwerkstatt */}
                        {isFirstLiederwerkstatt && (
                          <div className="hidden sm:flex items-center mr-2">
                            <div className="h-6 w-px bg-border mx-1" />
                          </div>
                        )}
                        {/* Group divider before Ausbildung */}
                        {isFirstAusbildung && (
                          <div className="hidden sm:flex items-center mr-2">
                            <div className="h-6 w-px bg-border mx-1" />
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setSelectedTyp(typ);
                            if (typ === "alle") {
                              searchParams.delete('kategorie');
                            } else {
                              searchParams.set('kategorie', typ);
                            }
                            setSearchParams(searchParams);
                          }}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            selectedTyp === typ
                              ? "bg-primary text-primary-foreground shadow-md"
                              : `bg-muted text-foreground/70 hover:bg-muted/80 hover:text-foreground ${groupStyles}`
                          }`}
                        >
                          {typ === "alle" ? "Alle Termine" : typ}
                        </button>
                      </div>
                    );
                  })}
                </div>


                {/* View Toggle */}
                <div className="flex items-center gap-2 justify-center">
                  <span className="text-sm font-medium">Ansicht:</span>
                  <div className="flex border rounded-md">
                    <Button
                      variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className="rounded-r-none"
                    >
                      <Grid3x3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className="rounded-none border-x"
                    >
                      <List className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('calendar')}
                      className="rounded-l-none"
                    >
                      <CalendarDays className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {filteredTermine.length === 0 && infosGefiltert(infoveranstaltungen, selectedTyp, quickFilter, searchQuery).length === 0 ? (
                <Card className="max-w-2xl mx-auto">
                  <CardContent className="text-center py-12">
                    <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Keine Termine gefunden</h3>
                    <p className="text-muted-foreground mb-4">
                      Mit den aktuellen Filtern wurden keine Termine gefunden.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setQuickFilter('alle');
                        setSelectedTyp('alle');
                        setSearchQuery('');
                      }}
                    >
                      Filter zurücksetzen
                    </Button>
                  </CardContent>
                </Card>
              ) : viewMode === 'calendar' ? (
                <div className="max-w-7xl mx-auto">
                  {/* Calendar Header */}
                  <div className="flex items-center justify-between mb-6 bg-card rounded-lg p-4 shadow-sm">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <h2 className="text-2xl font-bold text-primary">
                      {format(calendarMonth, "MMMM yyyy", { locale: de })}
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="bg-card rounded-lg shadow-sm overflow-hidden">
                    {/* Weekday Headers */}
                    <div className="grid grid-cols-7 bg-muted/50 border-b">
                      {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                        <div key={day} className="p-2 text-center text-sm font-semibold text-muted-foreground">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7">
                      {getCalendarDays().map((day, index) => {
                        if (!day) {
                          return <div key={`empty-${index}`} className="min-h-24 border-b border-r p-2 bg-muted/20" />;
                        }

                        const dayTermine = getTermineForDay(day);
                        const dayInfos = infosGefiltert(infoveranstaltungen, selectedTyp, quickFilter, searchQuery).filter((i) =>
                          isSameDay(new Date(i.beginn), day),
                        );
                        const isToday = isSameDay(day, new Date());
                        const isCurrentMonth = isSameMonth(day, calendarMonth);

                        return (
                          <div
                            key={day.toISOString()}
                            className={`min-h-24 border-b border-r p-2 transition-colors ${
                              !isCurrentMonth ? 'bg-muted/20 text-muted-foreground' : 'hover:bg-muted/50'
                            } ${isToday ? 'bg-primary/5' : ''}`}
                          >
                            <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : ''}`}>
                              {format(day, 'd')}
                              {isToday && (
                                <span className="ml-1 text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                                  Heute
                                </span>
                              )}
                            </div>

                            {dayInfos.length > 0 && (
                              <div className="space-y-1 mb-1">
                                {dayInfos.map((info) => (
                                  <InfoKalenderEintrag key={`info-${info.termin_id}`} info={info} />
                                ))}
                              </div>
                            )}
                            {dayTermine.length > 0 && (
                              <div className="space-y-1">
                                {dayTermine.slice(0, 3).map((termin) => (
                                  <button
                                    key={termin.id}
                                    onClick={() => {
                                      const url = getTerminPrimaryUrl(termin);
                                      if (hasCustomLandingPage(termin) && url) {
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                      } else if (url) {
                                        navigate(url);
                                      } else {
                                        setDetailTermin(termin);
                                        setSearchParams({ termin: termin.id });
                                      }
                                    }}
                                    className="w-full text-left p-1.5 rounded text-xs bg-primary/10 hover:bg-primary/20 transition-colors border-l-2 border-primary"
                                  >
                                    <div className="font-medium line-clamp-1">{termin.titel}</div>
                                    <div className="text-muted-foreground flex items-center gap-1 mt-0.5">
                                      <Clock className="h-3 w-3" />
                                      {format(new Date(termin.datum), "HH:mm", { locale: de })}
                                    </div>
                                    {termin.zugangstyp === 'öffentlich_kostenlos' && (
                                      <Badge className="mt-1 text-[10px] h-4 px-1 bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 border-none">
                                        Kostenlos
                                      </Badge>
                                    )}
                                  </button>
                                ))}
                                {dayTermine.length > 3 && (
                                  <div className="text-xs text-muted-foreground text-center py-1">
                                    +{dayTermine.length - 3} weitere
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Calendar Legend */}
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-primary/10 border-l-2 border-primary rounded" />
                      <span>Event</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-primary/5 rounded" />
                      <span>Heute</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="h-4 px-1.5 bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 border-none text-[10px]">
                        Kostenlos
                      </Badge>
                      <span>Kostenloser Termin</span>
                    </div>
                  </div>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
                  {mischen(filteredTermine, infosGefiltert(infoveranstaltungen, selectedTyp, quickFilter, searchQuery), (t) => t.datum).map((eintrag) => {
                    if (eintrag.art === "info") {
                      return <InfoRasterKarte key={`info-${eintrag.info.termin_id}`} info={eintrag.info} countdown={getCountdown(eintrag.info.beginn)} />;
                    }
                    const termin = eintrag.termin;
                    return (
                    <Card 
                      key={termin.id} 
                      className="flex flex-col overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105 group"
                      onClick={() => {
                        const url = getTerminPrimaryUrl(termin);
                        if (hasCustomLandingPage(termin) && url) {
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } else if (url) {
                          navigate(url);
                        } else {
                          setDetailTermin(termin);
                          setSearchParams({ termin: termin.id });
                        }
                      }}
                    >
                      {termin.bild_url && (
                        <div className="relative">
                          <AspectRatio ratio={4 / 3} className="bg-muted overflow-hidden">
                            <img 
                              src={termin.bild_url} 
                              alt={termin.titel}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </AspectRatio>
                          {/* Category color overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                      <CardHeader className="p-4">
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {termin.typ}
                          </Badge>
                          {getCountdown(termin.datum) && (
                            <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/20">
                              {getCountdown(termin.datum)}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-base leading-tight">{termin.titel}</CardTitle>
                        {termin.beschreibung && (
                          <CardDescription className="line-clamp-1 text-xs">
                            {termin.beschreibung.replace(/<[^>]*>/g, '')}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col p-4 pt-0">
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center gap-2 text-xs">
                            <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{format(new Date(termin.datum), "d. MMM yyyy", { locale: de })}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span>{format(new Date(termin.datum), "HH:mm", { locale: de })} Uhr</span>
                          </div>
                        </div>
                        
                        {termin.zugangstyp === 'öffentlich_kostenpflichtig' && termin.preis_normal && (
                          isTerminFreeForUser(termin) ? (
                            <Badge className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-100 border-none mb-3">
                              Kostenlos als Mitglied
                            </Badge>
                          ) : (
                            <div className="bg-primary/5 rounded p-2 mb-3">
                              <div className="text-xs font-semibold text-primary">
                                {formatEurFromCents(termin.preis_normal)}
                              </div>
                            </div>
                          )
                        )}
                        
                        {termin.zugangstyp === 'öffentlich_kostenlos' && (
                          <Badge className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-100 border-none mb-3">
                            Kostenlos
                          </Badge>
                        )}
                        
                        {termin.zugangstyp === 'nur_ausbildung' && (
                          <Badge className="bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 hover:bg-amber-100 border-none mb-3">
                            Nur Ausbildung
                          </Badge>
                        )}
                        
                        {termin.zugangstyp === 'nur_kursteilnehmer' && (
                          <Badge className="bg-violet-50 dark:bg-violet-950 text-violet-700 dark:text-violet-300 hover:bg-violet-100 border-none mb-3">
                            Nur für Kursteilnehmer
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-8 max-w-7xl mx-auto">
                  {nachMonatGemischt(
                    filteredTermine,
                    infosGefiltert(infoveranstaltungen, selectedTyp, quickFilter, searchQuery),
                    (t) => t.datum,
                    (iso) => format(new Date(iso), "MMMM yyyy", { locale: de }),
                  ).map(({ monat: month, eintraege: monthTermine }) => (
                    <div key={month} className="space-y-4">
                      <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-bold text-primary">{month}</h2>
                        <div className="h-px bg-border flex-1" />
                        <span className="text-sm text-muted-foreground">
                          {monthTermine.length} {monthTermine.length === 1 ? 'Termin' : 'Termine'}
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        {monthTermine.map((eintrag) => {
                          if (eintrag.art === "info") {
                            return <InfoListenKarte key={`info-${eintrag.info.termin_id}`} info={eintrag.info} countdown={getCountdown(eintrag.info.beginn)} />;
                          }
                          const termin = eintrag.termin;
                          return (
                          <Card 
                            key={termin.id} 
                            className={`overflow-hidden hover:shadow-lg transition-all duration-300 border-l-4 ${getCategoryColor(termin.typ)} hover:scale-[1.02] cursor-pointer`}
                            onClick={() => {
                              const url = getTerminPrimaryUrl(termin);
                              if (hasCustomLandingPage(termin) && url) {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              } else if (url) {
                                navigate(url);
                              } else {
                                setDetailTermin(termin);
                                setSearchParams({ termin: termin.id });
                              }
                            }}
                          >
                            <div className="flex flex-col sm:flex-row">
                              {termin.bild_url && (
                                <div className="sm:w-64 sm:flex-shrink-0 overflow-hidden">
                                  <AspectRatio ratio={16 / 9} className="bg-muted sm:h-full">
                                    <img 
                                      src={termin.bild_url} 
                                      alt={termin.titel}
                                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    />
                                  </AspectRatio>
                                </div>
                              )}
                              <div className="flex-1 p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="flex-1 min-w-0 space-y-3">
                                  <div className="flex items-center flex-wrap gap-2">
                                    <Badge variant="secondary" className="text-xs">
                                      {termin.typ}
                                    </Badge>
                                    {termin.zugangstyp === 'öffentlich_kostenlos' && (
                                      <Badge className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-100 border-none">
                                        Kostenlos
                                      </Badge>
                                    )}
                                    {termin.zugangstyp === 'öffentlich_kostenpflichtig' && termin.preis_normal && (
                                      isTerminFreeForUser(termin) ? (
                                        <Badge className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-100 border-none">
                                          Kostenlos als Mitglied
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                                          {formatEurFromCents(termin.preis_normal)}
                                        </Badge>
                                      )
                                    )}
                                    {termin.zugangstyp === 'nur_ausbildung' && (
                                      <Badge className="bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 hover:bg-amber-100 border-none">
                                        Nur Ausbildung
                                      </Badge>
                                    )}
                                    {termin.zugangstyp === 'nur_kursteilnehmer' && (
                                      <Badge className="bg-violet-50 dark:bg-violet-950 text-violet-700 dark:text-violet-300 hover:bg-violet-100 border-none">
                                        Nur für Kursteilnehmer
                                      </Badge>
                                    )}
                                    {getCountdown(termin.datum) && (
                                      <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">
                                        {getCountdown(termin.datum)}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <h3 className="font-bold text-lg leading-tight">{termin.titel}</h3>
                                  
                                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="h-4 w-4" />
                                      <span className="font-medium">{format(new Date(termin.datum), "EEEE, d. MMMM", { locale: de })}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock className="h-4 w-4" />
                                      <span className="font-medium">{format(new Date(termin.datum), "HH:mm", { locale: de })} Uhr</span>
                                    </div>
                                    {termin.max_teilnehmer && (
                                      <div className="flex items-center gap-2">
                                        <Users className="h-4 w-4" />
                                        <span>Max. {termin.max_teilnehmer} Teilnehmer</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {termin.beschreibung && (
                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                      {termin.beschreibung.replace(/<[^>]*>/g, '')}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="lg:flex-shrink-0 flex flex-col sm:flex-row lg:flex-col gap-2 lg:min-w-[140px]">
                                  <Button 
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const url = getTerminPrimaryUrl(termin);
                                      if (hasCustomLandingPage(termin) && url) {
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                      } else if (url) {
                                        navigate(url);
                                      } else {
                                        setDetailTermin(termin);
                                        setSearchParams({ termin: termin.id });
                                      }
                                    }}
                                    className="flex-1 lg:flex-none"
                                  >
                                    <Info className="h-4 w-4 mr-2" />
                                    Mehr Infos
                                  </Button>
                                  <Button 
                                    size="sm"
                                    variant={termin.zugangstyp === 'nur_ausbildung' || termin.zugangstyp === 'nur_kursteilnehmer' ? 'secondary' : 'default'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBookTermin(termin);
                                    }}
                                    disabled={bookingInProgress === termin.id}
                                    className="flex-1 lg:flex-none"
                                  >
                                    {bookingInProgress === termin.id ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : null}
                                    {termin.zugangstyp === 'nur_ausbildung' ? 'Zum Webinar' : termin.zugangstyp === 'nur_kursteilnehmer' ? 'Kostenlos anmelden' : isTerminFreeForUser(termin) ? 'Kostenlos anmelden' : 'Anmelden'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Enhanced Detail Dialog */}
          <Dialog open={!!detailTermin} onOpenChange={(open) => {
            if (!open) {
              setDetailTermin(null);
              setSearchParams({});
            }
          }}>
            <DialogContent className="max-w-3xl max-h-[85vh] md:max-h-[90vh] overflow-hidden flex flex-col p-0">
              {detailTermin && (
                <>
                  {/* Dialog Header with Gradient */}
                  <div className="px-6 pt-6 pb-4 border-b shrink-0 bg-gradient-to-r from-primary/5 to-accent/5">
                    <DialogHeader>
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <DialogTitle className="text-2xl flex-1">{detailTermin.titel}</DialogTitle>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => shareTermin(detailTermin)}
                            className="flex-shrink-0"
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadICS(detailTermin)}
                            className="flex-shrink-0"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1 pt-2">
                        {detailTermin.dozent && (
                          <div className="text-base text-foreground">
                            <span className="font-semibold">Dozent:</span> {detailTermin.dozent}
                          </div>
                        )}
                        {detailTermin.zielgruppe && (
                          <div className="text-sm text-muted-foreground">
                            <span className="font-semibold">Zielgruppe:</span> {detailTermin.zielgruppe}
                          </div>
                        )}
                      </div>
                    </DialogHeader>
                  </div>
                  
                  <div className="overflow-y-auto flex-1 px-6 py-4">
                    {/* Media Section */}
                    {detailTermin.video_url ? (
                      <div className="flex justify-center mb-6">
                        <div className="w-full max-w-2xl">
                          <AspectRatio ratio={16 / 9} className="bg-muted rounded-lg overflow-hidden">
                            <iframe
                              src={getVideoEmbedUrl(detailTermin.video_url) || undefined}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </AspectRatio>
                        </div>
                      </div>
                    ) : detailTermin.bild_url ? (
                      <div className="flex justify-center mb-6">
                        <div className="w-full max-w-2xl">
                          <AspectRatio ratio={16 / 9} className="bg-muted rounded-lg overflow-hidden">
                            <img 
                              src={detailTermin.bild_url} 
                              alt={detailTermin.titel}
                              className="w-full h-full object-cover"
                            />
                          </AspectRatio>
                        </div>
                      </div>
                    ) : null}

                    <div className="space-y-4">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10">
                        <span className="text-sm font-semibold text-primary">
                          {detailTermin.typ}
                        </span>
                      </div>

                      <div className="space-y-3 border-t pt-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                          <span className="font-medium">
                            {format(new Date(detailTermin.datum), "EEEE, d. MMMM yyyy", { locale: de })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-muted-foreground" />
                          <span>
                            {format(new Date(detailTermin.datum), "HH:mm", { locale: de })} Uhr
                            <span className="text-muted-foreground ml-2">({detailTermin.dauer_minuten} Minuten)</span>
                          </span>
                        </div>
                        {detailTermin.max_teilnehmer && (
                          <div className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-muted-foreground" />
                            <span>Maximal {detailTermin.max_teilnehmer} Teilnehmer</span>
                          </div>
                        )}
                      </div>

                      {detailTermin.beschreibung && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-lg mb-2">Beschreibung</h4>
                          <div 
                            className="prose prose-sm max-w-none text-muted-foreground"
                            dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(detailTermin.beschreibung) 
                            }}
                          />
                        </div>
                      )}

                      {detailTermin.inhalte && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-lg mb-3">Inhalte des Webinars</h4>
                          <ul className="space-y-2">
                            {detailTermin.inhalte.split('\n').filter(line => line.trim()).map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-primary mt-1">•</span>
                                <span className="text-muted-foreground">{item.trim()}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {detailTermin.sonstiges_ueberschrift && detailTermin.sonstiges_inhalt && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-lg mb-2">
                            {detailTermin.sonstiges_ueberschrift}
                          </h4>
                          <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-line">
                            {detailTermin.sonstiges_inhalt}
                          </div>
                        </div>
                      )}

                      {detailTermin.zugangstyp === 'öffentlich_kostenpflichtig' && (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="flex justify-between items-center px-4 py-3 bg-muted">
                            <span className="font-semibold">Preis</span>
                            <span className="font-semibold text-lg">{formatEurFromCents(detailTermin.preis_normal)}</span>
                          </div>
                          {/* Nur, wenn Mitglieder diesen Termin wirklich kostenlos buchen (dieselbe Regel wie die Kasse,
                              memberFreeTermin.ts); vorher stand der Satz bei jedem kostenpflichtigen Termin. */}
                          {isMemberFreeTerminTyp(detailTermin.typ) && (
                            <div className="px-4 py-3 bg-green-50 dark:bg-green-950 space-y-2">
                              <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-green-600 dark:text-green-400" />
                                <span className="text-sm text-green-800 dark:text-green-200">
                                  <strong>Mit Mitgliedschaft</strong> – kostenlose Teilnahme
                                </span>
                              </div>
                              <Link 
                                to="/preise" 
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                Mehr Infos zur Mitgliedschaft
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Link>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Info-Link */}
                      {detailTermin.info_link_url && (
                        <div className="mb-4">
                          <Button
                            variant="secondary"
                            className="w-full"
                            asChild
                          >
                            <a href={detailTermin.info_link_url} target="_blank" rel="noopener noreferrer">
                              {detailTermin.info_link_text || 'Weitere Informationen'}
                              <Info className="ml-2 h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      )}

                      {/* Registration / Info Section */}
                      {detailTermin.zugangstyp === 'nur_ausbildung' ? (
                        <div className="space-y-3 border rounded-lg p-4 bg-amber-50 dark:bg-amber-950">
                          {detailTermin.ausbildungs_info_text && (
                            <p className="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-line">
                              {detailTermin.ausbildungs_info_text}
                            </p>
                          )}
                          
                          {/* Button for Ausbildungsteilnehmer to add to their calendar */}
                          {currentUser && isAusbildungsteilnehmer ? (
                            addedToCalendar.has(detailTermin.id) ? (
                              <Button className="w-full" variant="outline" disabled>
                                <Check className="h-4 w-4 mr-2" />
                                In deiner Übersicht
                              </Button>
                            ) : (
                              <Button 
                                className="w-full" 
                                variant="default"
                                onClick={() => handleAddToCalendar(detailTermin)}
                                disabled={reminderInProgress === detailTermin.id}
                              >
                                {reminderInProgress === detailTermin.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Wird hinzugefügt...
                                  </>
                                ) : (
                                  <>
                                    <CalendarPlus className="h-4 w-4 mr-2" />
                                    Zu meinen Terminen hinzufügen
                                  </>
                                )}
                              </Button>
                            )
                          ) : (
                            <Button asChild className="w-full" variant="default">
                              <a 
                                href={detailTermin.ausbildungs_link || detailTermin.info_link_url || 'https://www.musikonzept.de/mein-bereich/bereich/fachkraft-fuer-musik'} 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                Infos zur Teilnahme
                                <ExternalLink className="ml-2 h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                      ) : (
                        <>
                          {(detailTermin.zugangstyp === 'öffentlich_kostenlos' || isTerminFreeForUser(detailTermin)) && (
                            <div className="text-center py-2 bg-green-50 dark:bg-green-950 rounded-lg mb-3">
                              <span className="text-lg font-bold text-green-700 dark:text-green-300">
                                {isTerminFreeForUser(detailTermin) && detailTermin.zugangstyp !== 'öffentlich_kostenlos' ? 'Kostenlos als Mitglied' : 'Kostenlose Teilnahme'}
                              </span>
                            </div>
                          )}
                          
                          <Button 
                            className="w-full" 
                            size="lg"
                            onClick={() => handleBookTermin(detailTermin)}
                            disabled={bookingInProgress === detailTermin.id}
                          >
                            {bookingInProgress === detailTermin.id ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Wird verarbeitet...
                              </>
                            ) : (
                              <>
                                {isTerminFreeForUser(detailTermin) ? "Kostenlos anmelden" : "Jetzt anmelden"}
                                <ExternalLink className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        </>
                      )}

                      {/* Related Events */}
                      {getRelatedTermine(detailTermin).length > 0 && (
                        <div className="border-t pt-4 mt-6">
                          <h4 className="font-semibold text-lg mb-3">Ähnliche Termine</h4>
                          <div className="grid gap-3">
                            {getRelatedTermine(detailTermin).map((relatedTermin) => (
                              <Card
                                key={relatedTermin.id}
                                className="p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={() => {
                                  if (relatedTermin.slug) {
                                    navigate(`/termin/${relatedTermin.slug}`);
                                  } else {
                                    setDetailTermin(relatedTermin);
                                    setSearchParams({ termin: relatedTermin.id });
                                  }
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  {relatedTermin.bild_url && (
                                    <img
                                      src={relatedTermin.bild_url}
                                      alt={relatedTermin.titel}
                                      className="w-16 h-16 rounded object-cover"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-medium text-sm line-clamp-1">{relatedTermin.titel}</h5>
                                    <p className="text-xs text-muted-foreground">
                                      {format(new Date(relatedTermin.datum), "d. MMM yyyy, HH:mm", { locale: de })} Uhr
                                    </p>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* Guest Booking Dialog for Free Termine */}
          <Dialog open={!!guestBookingTermin} onOpenChange={(open) => {
            if (!open) {
              setGuestBookingTermin(null);
              setGuestEmail("");
              setGuestFirstName("");
              setGuestLastName("");
              setGuestPhone("");
              setGuestStreet("");
              setGuestCity("");
              setGuestZip("");
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Kostenlos anmelden</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Bitte fülle das Formular aus, damit wir dir die Bestätigung und den Zugangslink senden können.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="guest-first-name">Vorname *</Label>
                    <Input
                      id="guest-first-name"
                      type="text"
                      placeholder="Dein Vorname"
                      value={guestFirstName}
                      onChange={(e) => setGuestFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="guest-last-name">Nachname *</Label>
                    <Input
                      id="guest-last-name"
                      type="text"
                      placeholder="Dein Nachname"
                      value={guestLastName}
                      onChange={(e) => setGuestLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="guest-email">E-Mail-Adresse *</Label>
                  <Input
                    id="guest-email"
                    type="email"
                    placeholder="deine@email.de"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="guest-phone">Telefon (optional)</Label>
                  <Input
                    id="guest-phone"
                    type="tel"
                    placeholder="+49 123 456789"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="guest-street">Straße + Hausnummer *</Label>
                  <Input
                    id="guest-street"
                    type="text"
                    placeholder="Musterstraße 123"
                    value={guestStreet}
                    onChange={(e) => setGuestStreet(e.target.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="guest-zip">PLZ *</Label>
                    <Input
                      id="guest-zip"
                      type="text"
                      placeholder="12345"
                      value={guestZip}
                      onChange={(e) => setGuestZip(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="guest-city">Ort *</Label>
                    <Input
                      id="guest-city"
                      type="text"
                      placeholder="Musterstadt"
                      value={guestCity}
                      onChange={(e) => setGuestCity(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <Button 
                  className="w-full"
                  disabled={
                    !guestEmail || 
                    !guestFirstName || 
                    !guestLastName || 
                    !guestStreet || 
                    !guestCity || 
                    !guestZip ||
                    bookingInProgress === guestBookingTermin?.id
                  }
                  onClick={() => {
                    if (guestBookingTermin && guestEmail && guestFirstName && guestLastName && guestStreet && guestCity && guestZip) {
                      // Validate email format
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(guestEmail.trim())) {
                        toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
                        return;
                      }
                      handleBookTermin(guestBookingTermin, {
                        email: guestEmail.trim(),
                        firstName: guestFirstName.trim(),
                        lastName: guestLastName.trim(),
                        phone: guestPhone.trim() || undefined,
                        street: guestStreet.trim(),
                        city: guestCity.trim(),
                        zip: guestZip.trim(),
                      });
                    }
                  }}
                >
                  {bookingInProgress === guestBookingTermin?.id ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Wird angemeldet...
                    </>
                  ) : (
                    'Kostenlos anmelden'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Success Dialog after booking */}
          <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
            <DialogContent className="max-w-md text-center">
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <DialogHeader className="text-center">
                  <DialogTitle className="text-xl">Anmeldung erfolgreich!</DialogTitle>
                </DialogHeader>
                <p className="text-muted-foreground">
                  Du hast dich erfolgreich für <strong>„{successTerminTitle}"</strong> angemeldet.
                </p>
                <div className="bg-primary/10 rounded-lg p-4 w-full">
                  <p className="text-sm font-medium text-primary">
                    📧 Schau jetzt in dein E-Mail-Postfach!
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Wir haben dir eine Bestätigungs-E-Mail mit allen wichtigen Informationen geschickt.
                  </p>
                </div>
                <Button 
                  className="w-full mt-2" 
                  onClick={() => setSuccessDialogOpen(false)}
                >
                  Verstanden
                </Button>
              </div>
            </DialogContent>
          </Dialog>


          {/* Back to Top Button */}
          {showBackToTop && (
            <Button
              onClick={scrollToTop}
              size="icon"
              className="fixed bottom-8 right-8 rounded-full shadow-lg z-50 animate-fade-in"
              aria-label="Zurück nach oben"
            >
              <ArrowUp className="h-5 w-5" />
            </Button>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Termine;